import { useOrderData } from 'hooks/orderForms/useOrderData';
import { usePartySelection } from 'hooks/orderForms/usePartySelection';
import { useOrderForm } from 'services/repositories/orderForms/orderForms';
import { usePartnerParties } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';

import { PartyDTO, PartyIdentifierDTO } from '../../../../support/types';
import { Divider } from '../OrderBreakdown/components/Divider';
import { BillingParty } from './components/BillingParty';
import { DeliveryParty } from './components/DeliveryParty';
import { OrderNotes } from './components/OrderNotes';
import { PlaceOrderButton } from './components/PlaceOrderButton';
import { Terms } from './components/Terms';

export function CheckoutForm({
  supplierSlug,
  onSubmit,
  isSubmitting,
}: {
  supplierSlug: string;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}) {
  const { data: currentUser } = useCurrentUser();
  const [orderData, setOrderData] = useOrderData(supplierSlug);

  const { data: orderFormData } = useOrderForm({
    variables: {
      supplierSlugName: supplierSlug,
    },
  });

  const { data: buyerParties } = usePartnerParties({
    variables: {
      partnerId: orderFormData?.data?.customer.id,
    },
  });
  const buyerCustomerIdentifier: PartyIdentifierDTO | undefined = buyerParties?.reduce<PartyIdentifierDTO | undefined>(
    (identifier, party: PartyDTO) =>
      identifier ||
      party.identifiers?.find(
        (identifier: PartyIdentifierDTO) =>
          identifier.domain === 'internal' && identifier.domain_partner_id === orderFormData?.data?.supplier.id,
      ),
    undefined,
  );

  const {
    selectedBillingPartyId,
    setSelectedBillingPartyId,
    selectedDeliveryPartyId,
    setSelectedDeliveryPartyId,
    handleAddParty,
    handleEditParty,
    partyError,
    clearPartyError,
    isSubmittingParty,
  } = usePartySelection({
    supplierSlug,
    currentUser,
    buyerParties,
    orderFormData,
    buyerId: orderFormData?.data?.customer.id,
    customerIdentifier: buyerCustomerIdentifier,
  });

  const hasSelectedBothParties = !!(selectedBillingPartyId && selectedDeliveryPartyId);

  return (
    <div className="grid grid-cols-[240px_1fr] gap-x-12 gap-y-4">
      <Divider className="col-span-2" />
      <BillingParty
        disabled={isSubmitting}
        parties={buyerParties}
        selectedPartyId={selectedBillingPartyId ?? undefined}
        onSelectParty={setSelectedBillingPartyId}
        onAddParty={(party, onSuccess) => handleAddParty(party, 'billing', onSuccess)}
        onEditParty={handleEditParty}
        error={partyError}
        clearError={clearPartyError}
        isLoading={isSubmittingParty}
      />
      <Divider className="col-span-2" />
      <DeliveryParty
        disabled={isSubmitting}
        parties={buyerParties}
        selectedPartyId={selectedDeliveryPartyId ?? undefined}
        onSelectParty={setSelectedDeliveryPartyId}
        onAddParty={(party, onSuccess) => handleAddParty(party, 'delivery', onSuccess)}
        onEditParty={handleEditParty}
        error={partyError}
        clearError={clearPartyError}
        isLoading={isSubmittingParty}
      />
      <Divider className="col-span-2" />
      <OrderNotes
        disabled={isSubmitting}
        value={orderData.comments}
        onChange={(comments) => setOrderData({ ...orderData, comments })}
      />
      <Divider className="col-span-2" />
      <div className="col-start-2 flex flex-col gap-3">
        <PlaceOrderButton disabled={!hasSelectedBothParties} isPlacingOrder={isSubmitting} onClick={onSubmit} />
        <Terms terms={orderFormData?.data?.terms} />
      </div>
    </div>
  );
}
